import type { FirebaseApp } from 'firebase/app';
import type { Auth } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import type { FirebaseStorage } from 'firebase/storage';
import type { Functions } from 'firebase/functions';

import { getFirebaseConfig } from './config';

let appInstance: FirebaseApp | null = null;
let authInstance: Auth | null = null;
let firestoreInstance: Firestore | null = null;
let storageInstance: FirebaseStorage | null = null;
let functionsInstance: Functions | null = null;
let appCheckInitialized = false;

let appModulePromise: Promise<typeof import('firebase/app')> | null = null;
const loadAppModule = () => {
  if (!appModulePromise) {
    appModulePromise = import('firebase/app');
  }
  return appModulePromise;
};

const initializeAppCheckIfConfigured = async (app: FirebaseApp): Promise<void> => {
  if (appCheckInitialized) {
    return;
  }

  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  if (!siteKey) {
    return;
  }

  if (import.meta.env.DEV) {
    const debugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
    if (debugToken) {
      (globalThis as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN =
        debugToken === 'true' ? true : debugToken;
    }
  }

  const { initializeAppCheck, ReCaptchaV3Provider } = await import('firebase/app-check');
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
  appCheckInitialized = true;
};

const initializeFirebaseApp = async (): Promise<FirebaseApp> => {
  if (appInstance) {
    return appInstance;
  }

  const { getApps, initializeApp } = await loadAppModule();

  if (getApps().length > 0) {
    appInstance = getApps()[0]!;
    void initializeAppCheckIfConfigured(appInstance);
    return appInstance;
  }

  const config = getFirebaseConfig();
  appInstance = initializeApp(config);
  void initializeAppCheckIfConfigured(appInstance);
  return appInstance;
};

export const getFirebaseAuth = async (): Promise<Auth> => {
  if (authInstance) {
    return authInstance;
  }
  const [{ getAuth, setPersistence, browserLocalPersistence, connectAuthEmulator }, app] = await Promise.all([
    import('firebase/auth'),
    initializeFirebaseApp(),
  ]);
  const auth = getAuth(app);
  await setPersistence(auth, browserLocalPersistence);
  if (import.meta.env.DEV && import.meta.env.VITE_USE_AUTH_EMULATOR === 'true') {
    const host = import.meta.env.VITE_AUTH_EMULATOR_HOST ?? '127.0.0.1';
    const port = Number.parseInt(import.meta.env.VITE_AUTH_EMULATOR_PORT ?? '9099', 10);
    connectAuthEmulator(auth, `http://${host}:${port}`, { disableWarnings: true });
  }
  authInstance = auth;
  return authInstance;
};

export const getFirebaseFirestore = async (): Promise<Firestore> => {
  if (firestoreInstance) {
    return firestoreInstance;
  }
  const [{ getFirestore }, app] = await Promise.all([
    import('firebase/firestore'),
    initializeFirebaseApp(),
  ]);
  firestoreInstance = getFirestore(app);
  return firestoreInstance;
};

export const getFirebaseStorage = async (): Promise<FirebaseStorage> => {
  if (storageInstance) {
    return storageInstance;
  }
  const [{ getStorage }, app] = await Promise.all([
    import('firebase/storage'),
    initializeFirebaseApp(),
  ]);
  storageInstance = getStorage(app);
  return storageInstance;
};

export const getFirebaseFunctions = async (): Promise<Functions> => {
  if (functionsInstance) {
    return functionsInstance;
  }

  const [{ getFunctions, connectFunctionsEmulator }, app] = await Promise.all([
    import('firebase/functions'),
    initializeFirebaseApp(),
  ]);

  const region = import.meta.env.VITE_FIREBASE_REGION ?? 'us-central1';
  const customDomain = import.meta.env.VITE_FIREBASE_FUNCTIONS_DOMAIN;

  functionsInstance = customDomain ? getFunctions(app, customDomain) : getFunctions(app, region);

  if (import.meta.env.DEV && import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === 'true') {
    const host = import.meta.env.VITE_FUNCTIONS_EMULATOR_HOST ?? '127.0.0.1';
    const port = Number.parseInt(import.meta.env.VITE_FUNCTIONS_EMULATOR_PORT ?? '5001', 10);
    connectFunctionsEmulator(functionsInstance, host, port);
  }

  return functionsInstance;
};
